import React, { useState, useEffect } from "react";
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Grid, Alert, Snackbar,
  Chip, IconButton, Tooltip, FormControl, InputLabel, Select, MenuItem,
  InputAdornment, Divider, Switch, FormControlLabel
} from "@mui/material";
import { Add, Edit, Delete, Refresh, Search, Visibility } from "@mui/icons-material";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "";

const emptyForm = {
  // 1. Identificación
  codigo: "", codigo_auxiliar: "", nombre: "",
  // 2. Clasificación
  tipo: "standard", categoria_id: "", marca_id: "", iva_porcentaje: "15", iva_codigo_sri: "2",
  // 3. Precios
  precio_1: 0, precio_2: 0, precio_3: 0, costo_ultima_compra: 0,
  // 4. Inventario
  stock_total: 0,
  // 5. Descripción e Imagen
  descripcion_corta: "", ficha_tecnica: "", imagen_url: "",
  // 6. Cloud / Prestashop
  sincronizar_web: true,
  producto_especial: false,
  prestashop_product_id: null, estado_cloud: "pendiente_actualizar",
};

export default function Productos() {
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando, setEditando] = useState(false);
  const [seleccionado, setSeleccionado] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [busqueda, setBusqueda] = useState("");
  const [formData, setFormData] = useState({ ...emptyForm });
  const [verProducto, setVerProducto] = useState(null);
  const [page, setPage] = useState(0);
  const POR_PAGINA = 20;
  const [categorias, setCategorias] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [bodegas, setBodegas] = useState([]);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });
  useEffect(() => { cargar(); cargarCatalogoData(); }, [busqueda]);

  const cargarCatalogoData = async () => {
    try {
      const [catRes, marRes, bodRes] = await Promise.all([
        axios.get(`${API_URL}/api/catalogo/categorias`, { headers: headers() }),
        axios.get(`${API_URL}/api/catalogo/marcas`, { headers: headers() }),
        axios.get(`${API_URL}/api/bodegas`, { headers: headers() }),
      ]);
      setCategorias(Array.isArray(catRes.data) ? catRes.data : []);
      setMarcas(Array.isArray(marRes.data) ? marRes.data : []);
      setBodegas(bodRes.data?.data || []);
    } catch {}
  };

  const cargar = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ tipo: "PRODUCTO" });
      if (busqueda) params.append("busqueda", busqueda);
      const res = await axios.get(`${API_URL}/api/productos?${params}`, { headers: headers() });
      setProductos(Array.isArray(res.data) ? res.data : []);
    } catch { setProductos([]); }
    setLoading(false);
  };

  const handleInput = (campo, valor) => {
    const upper = ["nombre"];
    const v = upper.includes(campo) && typeof valor === "string" ? valor.toUpperCase() : valor;
    setFormData(prev => ({ ...prev, [campo]: v }));
  };

  const abrirDialogo = (p = null) => {
    if (p) {
      setEditando(true); setSeleccionado(p);
      setFormData({
        ...emptyForm,
        codigo: p.codigo || "",
        codigo_auxiliar: p.codigo_auxiliar_sku || p.codigo_auxiliar || "",
        nombre: p.nombre || "",
        tipo: p.tipo === "SERVICIO" ? "virtual" : "standard",
        categoria_id: p.categoria_id || "",
        marca_id: p.marca_id || "",
        iva_porcentaje: (p.iva_porcentaje || "15").replace("%", ""),
        iva_codigo_sri: p.iva_codigo_sri || "2",
        precio_1: parseFloat(p.precio_1 || p.precio_venta || 0),
        precio_2: parseFloat(p.precio_2 || 0),
        precio_3: parseFloat(p.precio_3 || 0),
        costo_ultima_compra: parseFloat(p.costo_ultima_compra || p.precio_compra || 0),
        stock_total: parseFloat(p.cantidad_stock || p.stock_actual || p.stock_total || 0),
        sincronizar_web: p.sincronizar_web !== false,
        producto_especial: p.producto_especial || false,
        descripcion_corta: p.descripcion_corta || p.descripcion || "",
        ficha_tecnica: p.ficha_tecnica || "",
        imagen_url: p.imagen_url || "",
        prestashop_product_id: p.prestashop_product_id || null,
        estado_cloud: p.estado_cloud || "pendiente_actualizar",
      });
    } else { setEditando(false); setSeleccionado(null); setFormData({ ...emptyForm }); }
    setDialogOpen(true);
  };
  const cerrarDialogo = () => { setDialogOpen(false); setEditando(false); setSeleccionado(null); };

  const guardar = async () => {
    if (!formData.nombre) return showSnackbar("El nombre es requerido", "error");
    try {
      const url = editando ? `${API_URL}/api/productos/${seleccionado.id}` : `${API_URL}/api/productos`;
      const method = editando ? "put" : "post";
      const data = {
        ...formData,
        tipo: "PRODUCTO",
        codigo_auxiliar_sku: formData.codigo_auxiliar,
        iva_porcentaje: formData.iva_porcentaje + "%",
        precio_venta: formData.precio_1,
      };
      await axios[method](url, data, { headers: headers() });
      showSnackbar(editando ? "Producto actualizado" : "Producto creado", "success");
      cerrarDialogo(); cargar();
    } catch (e) { showSnackbar(e.response?.data?.error || "Error al guardar", "error"); }
  };

  const eliminar = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.nombre}"?`)) return;
    try { await axios.delete(`${API_URL}/api/productos/${p.id}`, { headers: headers() }); showSnackbar("Eliminado", "success"); cargar(); }
    catch (e) { showSnackbar(e.response?.data?.error || "Error", "error"); }
  };

  const showSnackbar = (message, severity) => setSnackbar({ open: true, message, severity });

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h5" fontWeight={700}>📦 Productos</Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" startIcon={<Refresh />} onClick={cargar}>Actualizar</Button>
            <Button variant="contained" startIcon={<Add />} onClick={() => abrirDialogo()}>Nuevo Producto</Button>
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <TextField fullWidth size="small" placeholder="Buscar por código, SKU o nombre..."
            value={busqueda} onChange={(e) => { setBusqueda(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} /> }} />
        </Box>

        {loading ? <Typography>Cargando...</Typography> : productos.length === 0 ? (
          <Alert severity="info">No hay productos registrados.</Alert>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                    <TableCell><strong>Código</strong></TableCell>
                    <TableCell><strong>SKU</strong></TableCell>
                    <TableCell align="center"><strong>Img</strong></TableCell>
                    <TableCell><strong>Nombre</strong></TableCell>
                    <TableCell><strong>Categoría</strong></TableCell>
                    <TableCell align="right"><strong>PVP</strong></TableCell>
                    <TableCell align="right"><strong>P2</strong></TableCell>
                    <TableCell align="right"><strong>P3</strong></TableCell>
                    <TableCell align="right"><strong>Costo</strong></TableCell>
                    <TableCell align="center"><strong>Stock</strong></TableCell>
                    <TableCell align="center"><strong>Acciones</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {productos.slice(page * POR_PAGINA, (page + 1) * POR_PAGINA).map((p) => {
                    const pvp = parseFloat(p.pvp || p.precio_venta || p.precio_1 || 0);
                    const p2 = parseFloat(p.precio_2 || 0);
                    const p3 = parseFloat(p.precio_3 || 0);
                    const costo = parseFloat(p.costo_ultima_compra || p.precio_compra || 0);
                    return (
                      <TableRow key={p.id} hover>
                        <TableCell><Typography variant="body2" fontFamily="monospace">{p.codigo || "—"}</Typography></TableCell>
                        <TableCell><Typography variant="caption">{p.codigo_auxiliar_sku || p.codigo_auxiliar || "—"}</Typography></TableCell>
                        <TableCell align="center">
                          {p.imagen_url ? (
                            <img src={p.imagen_url.startsWith("http") ? p.imagen_url : `${API_URL}/${p.imagen_url}`}
                              alt="" style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 4 }} />
                          ) : (
                            <Box sx={{ width: 36, height: 36, bgcolor: "#f5f5f5", borderRadius: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell><Typography variant="body2" fontWeight={600}>{p.nombre}</Typography></TableCell>
                        <TableCell><Typography variant="caption">{categorias.find(c => c.id === p.categoria_id)?.nombre || "—"}</Typography></TableCell>
                        <TableCell align="right"><strong>${pvp.toFixed(2)}</strong></TableCell>
                        <TableCell align="right">${p2.toFixed(2)}</TableCell>
                        <TableCell align="right">${p3.toFixed(2)}</TableCell>
                        <TableCell align="right"><Typography variant="caption">${costo.toFixed(2)}</Typography></TableCell>
                        <TableCell align="center"><Typography variant="body2">{parseFloat(p.cantidad_stock || p.stock_actual || p.stock_total || 0)}</Typography></TableCell>
                        <TableCell align="center">
                          <Tooltip title="Ver"><IconButton size="small" onClick={() => setVerProducto(p)}><Visibility /></IconButton></Tooltip>
                          <Tooltip title="Editar"><IconButton size="small" onClick={() => abrirDialogo(p)}><Edit /></IconButton></Tooltip>
                          <Tooltip title="Eliminar"><IconButton size="small" color="error" onClick={() => eliminar(p)}><Delete /></IconButton></Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2, px: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {Math.min(page * POR_PAGINA + 1, productos.length)}–{Math.min((page + 1) * POR_PAGINA, productos.length)} de {productos.length}
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button variant="outlined" size="small" disabled={page === 0} onClick={() => setPage(page - 1)}>← Anterior</Button>
                <Button variant="outlined" size="small" disabled={(page + 1) * POR_PAGINA >= productos.length} onClick={() => setPage(page + 1)}>Siguiente →</Button>
              </Box>
            </Box>
          </>
        )}
      </Paper>

      {/* Dialog Crear/Editar */}
      <Dialog open={dialogOpen} onClose={cerrarDialogo} fullWidth PaperProps={{ sx: { borderRadius: 3, maxWidth: 900, width: "95vw" } }}>
        <DialogTitle sx={{ pb: 1, fontWeight: 700, fontSize: 20 }}>{editando ? "✏️ Editar Producto" : "➕ Nuevo Producto"}</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>

          {/* 1. Identificación */}
          <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ mb: 2 }}>Identificación y Códigos</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField fullWidth label="Código (Principal)" value={formData.codigo}
                onChange={(e) => handleInput("codigo", e.target.value)}
                inputProps={{ style: { fontSize: 16 } }} helperText="codigoPrincipal SRI" />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField fullWidth label="Código Auxiliar / SKU" value={formData.codigo_auxiliar}
                onChange={(e) => handleInput("codigo_auxiliar", e.target.value)}
                inputProps={{ style: { fontSize: 16 } }} helperText="Código de barras / EAN13" />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth required label="Nombre del Producto" value={formData.nombre}
                onChange={(e) => handleInput("nombre", e.target.value)}
                inputProps={{ style: { textTransform: "uppercase", fontSize: 16 }, maxLength: 300 }}
                helperText="Máx. 300 caracteres (límite SRI)" />
            </Grid>
          </Grid>

          {/* 2. Categoría y Marca */}
          <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ mb: 2 }}>Clasificación</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Categoría</InputLabel>
                <Select value={formData.categoria_id || ""} label="Categoría" onChange={(e) => handleInput("categoria_id", e.target.value)}>
                  <MenuItem value="">— Sin categoría —</MenuItem>
                  {categorias.map(c => <MenuItem key={c.id} value={c.id}>{c.nombre}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Marca</InputLabel>
                <Select value={formData.marca_id || ""} label="Marca" onChange={(e) => handleInput("marca_id", e.target.value)}>
                  <MenuItem value="">— Sin marca —</MenuItem>
                  {marcas.map(m => <MenuItem key={m.id} value={m.id}>{m.nombre}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* 3. Impuestos */}
          <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ mb: 2 }}>Impuestos</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>IVA</InputLabel>
                <Select value={formData.iva_porcentaje} label="IVA" onChange={(e) => {
                  handleInput("iva_porcentaje", e.target.value);
                  handleInput("iva_codigo_sri", e.target.value === "0" ? "0" : "2");
                }}>
                  <MenuItem value="15">15%</MenuItem>
                  <MenuItem value="0">0%</MenuItem>
                  <MenuItem value="noobj">No Objeto de IVA</MenuItem>
                  <MenuItem value="exento">Exento de IVA</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* 3. Precios */}
          <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ mb: 2 }}>Precios (IVA incluido)</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField fullWidth label="PVP" type="number"
                value={formData.pvp || ''}
                onChange={(e) => { const v = e.target.value === '' ? '' : parseFloat(e.target.value) || 0; handleInput("pvp", v); handleInput("precio_1", v); }}
                onFocus={(e) => { if (parseFloat(e.target.value) === 0) e.target.value = ''; }}
                onBlur={(e) => { if (e.target.value === '') { handleInput("pvp", 0); handleInput("precio_1", 0); } }}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                inputProps={{ min: 0, step: 0.01, style: { fontSize: 16 } }}
                helperText="Precio de venta al público" />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField fullWidth label="Precio 2 (Mayorista)" type="number"
                value={formData.precio_2 || ''}
                onChange={(e) => handleInput("precio_2", e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                onFocus={(e) => { if (parseFloat(e.target.value) === 0) e.target.value = ''; }}
                onBlur={(e) => { if (e.target.value === '') handleInput("precio_2", 0); }}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                inputProps={{ min: 0, step: 0.01, style: { fontSize: 16 } }} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField fullWidth label="Precio 3 (Especial)" type="number"
                value={formData.precio_3 || ''}
                onChange={(e) => handleInput("precio_3", e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                onFocus={(e) => { if (parseFloat(e.target.value) === 0) e.target.value = ''; }}
                onBlur={(e) => { if (e.target.value === '') handleInput("precio_3", 0); }}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                inputProps={{ min: 0, step: 0.01, style: { fontSize: 16 } }} />
            </Grid>
          </Grid>

          {/* Producto Especial */}
          <FormControlLabel
            sx={{ mb: 2 }}
            control={<Switch checked={formData.producto_especial} onChange={(e) => handleInput("producto_especial", e.target.checked)} color="warning" />}
            label={<Box><Typography variant="body2" fontWeight={600}>Producto Especial (precio modificable en POS)</Typography>
              <Typography variant="caption" color="text.secondary">El cajero puede cambiar el precio de venta al momento de facturar.</Typography></Box>}
          />

          {/* 4. Info (solo lectura) */}
          {editando && (
            <>
              <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ mb: 2 }}>Información (automático)</Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField fullWidth label="Costo Última Compra" value={`$${formData.costo_ultima_compra.toFixed(2)}`} disabled
                    helperText="Se actualiza con cada compra" />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField fullWidth label="Stock Total" value={formData.stock_total} disabled
                    helperText="Se actualiza con compras/ventas" />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField fullWidth label="Bodega" value={
                    bodegas.find(b => b.id === (seleccionado?.bodega_id || 'PRINCIPAL'))?.nombre || 'Principal'
                  } disabled helperText="Ubicación del producto" />
                </Grid>
              </Grid>
            </>
          )}

          {/* 5. Descripción y Ficha Técnica */}
          <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ mb: 2 }}>Descripción e Imagen</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth label="Descripción del producto" value={formData.descripcion_corta}
                onChange={(e) => handleInput("descripcion_corta", e.target.value)}
                inputProps={{ style: { fontSize: 16 } }} multiline rows={2}
                placeholder="Descripción opcional del producto" />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth label="Ficha Técnica" value={formData.ficha_tecnica || ""}
                onChange={(e) => handleInput("ficha_tecnica", e.target.value)}
                inputProps={{ style: { fontSize: 16 } }} multiline rows={2}
                placeholder="Especificaciones técnicas, materiales, medidas..." />
            </Grid>
            <Grid size={12}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Button variant="outlined" component="label" sx={{ height: 56 }}>
                  📷 Subir Imagen
                  <input type="file" hidden accept="image/*" onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    const formDataImg = new FormData();
                    formDataImg.append("imagen", file);
                    try {
                      const res = await axios.post(`${API_URL}/api/productos/upload-imagen`, formDataImg, {
                        headers: { ...headers(), "Content-Type": "multipart/form-data" }
                      });
                      if (res.data.url) handleInput("imagen_url", res.data.url);
                    } catch { showSnackbar("Error al subir imagen", "error"); }
                  }} />
                </Button>
                {formData.imagen_url && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <img src={formData.imagen_url.startsWith("http") ? formData.imagen_url : `${API_URL}/${formData.imagen_url}`}
                      alt="producto" style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 8, border: "1px solid #ddd" }} />
                    <Button size="small" color="error" onClick={() => handleInput("imagen_url", "")}>✕</Button>
                  </Box>
                )}
                {!formData.imagen_url && <Typography variant="caption" color="text.secondary">Sin imagen</Typography>}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={cerrarDialogo} color="inherit">Cancelar</Button>
          <Button onClick={guardar} variant="contained" size="large">{editando ? "Actualizar" : "Crear"} Producto</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Ver Detalle */}
      <Dialog open={!!verProducto} onClose={() => setVerProducto(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        {verProducto && (<>
          <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>👁️ {verProducto.nombre}</DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" color="primary" gutterBottom>Identificación</Typography>
                <Box sx={{ pl: 1, mb: 2 }}>
                  <Typography variant="body2"><strong>Código:</strong> {verProducto.codigo || "—"}</Typography>
                  <Typography variant="body2"><strong>SKU / Auxiliar:</strong> {verProducto.codigo_auxiliar_sku || verProducto.codigo_auxiliar || "—"}</Typography>
                  <Typography variant="body2"><strong>Nombre:</strong> {verProducto.nombre}</Typography>
                </Box>
                <Typography variant="subtitle2" color="primary" gutterBottom>Precios</Typography>
                <Box sx={{ pl: 1, mb: 2 }}>
                  <Typography variant="body2"><strong>PVP:</strong> ${parseFloat(verProducto.pvp || verProducto.precio_venta || verProducto.precio_1 || 0).toFixed(2)}</Typography>
                  <Typography variant="body2"><strong>Precio 2:</strong> ${parseFloat(verProducto.precio_2 || 0).toFixed(2)}</Typography>
                  <Typography variant="body2"><strong>Precio 3:</strong> ${parseFloat(verProducto.precio_3 || 0).toFixed(2)}</Typography>
                  <Typography variant="body2"><strong>Costo Compra:</strong> ${parseFloat(verProducto.costo_ultima_compra || verProducto.precio_compra || 0).toFixed(2)}</Typography>
                  <Typography variant="body2"><strong>IVA:</strong> {(verProducto.iva_porcentaje || "15").replace("%","")}%</Typography>
                </Box>
                <Typography variant="subtitle2" color="primary" gutterBottom>Inventario</Typography>
                <Box sx={{ pl: 1 }}>
                  <Typography variant="body2"><strong>Stock Total:</strong> {parseFloat(verProducto.cantidad_stock || verProducto.stock_actual || verProducto.stock_total || 0)}</Typography>
                  <Typography variant="body2"><strong>Bodega:</strong> {bodegas.find(b => b.id === (verProducto.bodega_id || 'PRINCIPAL'))?.nombre || 'Principal'}</Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                {/* Imagen */}
                {(verProducto.imagen_url) && (
                  <Box sx={{ mb: 2, textAlign: "center" }}>
                    <img src={verProducto.imagen_url.startsWith("http") ? verProducto.imagen_url : `${API_URL}/${verProducto.imagen_url}`}
                      alt={verProducto.nombre} style={{ maxWidth: "100%", maxHeight: 200, objectFit: "contain", borderRadius: 8, border: "1px solid #eee" }} />
                  </Box>
                )}
                <Typography variant="subtitle2" color="primary" gutterBottom>Ficha Técnica</Typography>
                <Box sx={{ pl: 1, mb: 2 }}>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{verProducto.ficha_tecnica || "—"}</Typography>
                </Box>
                <Typography variant="subtitle2" color="primary" gutterBottom>Descripción</Typography>
                <Box sx={{ pl: 1 }}>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{verProducto.descripcion_corta || verProducto.descripcion || "—"}</Typography>
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => { setVerProducto(null); abrirDialogo(verProducto); }} startIcon={<Edit />}>Editar</Button>
            <Button onClick={() => setVerProducto(null)} variant="contained">Cerrar</Button>
          </DialogActions>
        </>)}
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
